import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  buildAssistantChatPath,
  buildProjectChatPath,
  NEW_CHAT_PATH,
  NEW_CHAT_ROUTE
} from 'src/content/ChatV2/constants';
import {
  buildStartChatNavigation,
  chatFileToStartChatResource
} from 'src/content/ChatV2/lib/startChat';
import DocumentsContext from 'src/contexts/FileManagerContext';
import { usePermission } from 'src/hooks/usePermission';
import type { FavouriteModel } from 'src/layouts/SidebarV2/types';
import {
  favouriteToChatFileMapper,
  favouriteToModel
} from 'src/layouts/SidebarV2/utils';
import { setIsFavouritesFetching } from 'src/redux/slices/data';
import type { RootState } from 'src/redux/store';
import { useDispatch, useSelector } from 'src/redux/store';
import type { StringKeys } from 'src/types/base';
import { FAVOURITE_TYPE, FEATURE_PERMISSIONS } from 'src/types/enum';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';

export interface UseFavourites {
  groups: {
    kind: FAVOURITE_TYPE;
    heading: string;
    items: FavouriteModel[];
  }[];
  toggleFavourite: (item: FavouriteModel) => void;
  startFavouriteChat: (item: FavouriteModel) => void;
  isUpdating: boolean;
}

const numericId = (id: string) => Number(id.split('-').pop());

export const useFavourites = (): UseFavourites => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userFavourites, userAppPermissions, isFavouritesFetching, iconMap } =
    useSelector((state: RootState) => state.data);
  const { updateFavourite, isUpdatingFavourite } = useContext(DocumentsContext);
  const canUseWorkspacesSubfeature = usePermission(
    FEATURE_PERMISSIONS.WORKSPACES_SUBFEATURE
  );
  const canUseEnterpriseKnowledgeCapability = usePermission(
    FEATURE_PERMISSIONS.WORKSPACES_ENTERPRISE_KNOWLEDGE_CAPABILITY
  );
  const canShowWorkspaceFavourites =
    canUseWorkspacesSubfeature && canUseEnterpriseKnowledgeCapability;

  const findRaw = (type: string, id: number): StringKeys | undefined =>
    userFavourites
      ?.find((fav: StringKeys) => fav?.item_type === type)
      ?.items?.find((item: StringKeys) => item.item_id === id);

  const groups = useMemo(() => {
    const canView = !!userAppPermissions?.['view-root-folder-content']?.value;
    if (!canView) {
      return [];
    }
    const headings: Record<FAVOURITE_TYPE, string> = {
      [FAVOURITE_TYPE.ASSISTANT]: t(T.assistants),
      [FAVOURITE_TYPE.RESOURCE]: t(T.workspaces),
      [FAVOURITE_TYPE.PROJECT]: t(T.projects),
      [FAVOURITE_TYPE.ADMIN_CONSOLE]: t(T.adminConsole)
    };
    const order = [
      FAVOURITE_TYPE.ASSISTANT,
      FAVOURITE_TYPE.RESOURCE,
      FAVOURITE_TYPE.PROJECT
    ];
    return (userFavourites ?? [])
      .filter((fav: StringKeys) => order.includes(fav?.item_type))
      .filter(
        (fav: StringKeys) =>
          fav?.item_type !== FAVOURITE_TYPE.RESOURCE ||
          canShowWorkspaceFavourites
      )
      .map((fav: StringKeys) => ({
        kind: fav.item_type as FAVOURITE_TYPE,
        heading: headings[fav.item_type as FAVOURITE_TYPE],
        items: [...(fav.items ?? [])]
          .sort((a: StringKeys, b: StringKeys) =>
            (a.display_name || a.name).localeCompare(b.display_name || b.name)
          )
          .map((item: StringKeys) =>
            favouriteToModel(item, fav.item_type, iconMap)
          )
      }));
  }, [
    userFavourites,
    userAppPermissions,
    iconMap,
    t,
    canShowWorkspaceFavourites
  ]);

  const toggleFavourite = (item: FavouriteModel) => {
    if (isUpdatingFavourite || isFavouritesFetching) {
      return;
    }
    dispatch(setIsFavouritesFetching(true));
    void updateFavourite(numericId(item.id), !item.isFavourite, item.kind);
  };

  const startFavouriteChat = (item: FavouriteModel) => {
    const id = numericId(item.id);

    if (item.kind === FAVOURITE_TYPE.ASSISTANT) {
      navigate(buildAssistantChatPath(id, NEW_CHAT_ROUTE));
      return;
    }

    if (item.kind === FAVOURITE_TYPE.PROJECT) {
      navigate(buildProjectChatPath(id, NEW_CHAT_ROUTE), {
        state: { projectChatName: item.name }
      });
      return;
    }

    const raw = findRaw(FAVOURITE_TYPE.RESOURCE, id);
    if (!raw) {
      navigate(NEW_CHAT_PATH);
      return;
    }
    const { to, options } = buildStartChatNavigation([
      chatFileToStartChatResource(
        favouriteToChatFileMapper(
          raw as Parameters<typeof favouriteToChatFileMapper>[0]
        )
      )
    ]);
    navigate(to, options);
  };

  return {
    groups,
    toggleFavourite,
    startFavouriteChat,
    isUpdating: isUpdatingFavourite
  };
};
